/* Copied from indexx-exchange-backend/zodiac-embed/ (fingerprint 7975636513ee) — edit the source there, not here. */
"use client";
/*
 * Client component: this reads window, holds state and portals to <body>.
 * The pages-router and CRA products never needed the directive; Bitcoin Yay,
 * EMMM and ShoperPal are App Router, where a component without it is rendered
 * on the server and every hook in here is a build error.
 */
/*
 * Zodiac — the ecosystem switcher and Dashboard link.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ SOURCE OF TRUTH: zodiac-embed/ at the workspace root.                    │
 * │ Copied into each product repo by zodiac-embed/sync.sh. Do NOT edit a     │
 * │ copy — change the source and re-sync, or the eight drift apart.          │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Ported from the approved Zodiac demo (zodiac-frontend), which is reference
 * only. Two exports, because the bar wants them in different places:
 *
 *   <ZodiacLauncher />      the nine-oval grid + the word + the panel   (LEFT)
 *   <ZodiacDashboardLink /> the Dashboard link                         (RIGHT)
 *
 * ── Why no framework ───────────────────────────────────────────────────────
 *
 * The four Phase 1 repos disagree on everything: two CRA 4 with MUI/Ant, two
 * Next 13 with Bootstrap, none with Tailwind, ported from a Tailwind 4 / React
 * 19 demo. So this brings nothing with it — React 18 hooks, plain <a>, and one
 * CSS module. Cross-product links are external URLs anyway, so a router would
 * buy nothing even where one exists.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createAuth } from "./zodiacAuth";
import s from "./zodiac-launcher.module.css";

/**
 * Measured by fitting an ellipse to the Bitcoin Yay coin artwork, not eyeballed.
 * Every oval in the ecosystem uses it, including the nine in the grid.
 */
/*
 * What the switcher calls itself, in the tooltip and to assistive technology.
 * Names the product AND says what it does: "Indexx Zodiac" alone does not tell
 * a first-time user that this is how you move between products.
 */
const TRIGGER_LABEL = "Indexx Zodiac — switch products";

const OVAL_ANGLE = -38.4;

const GROUPS = [
  { id: "earn", label: "Earn" },
  { id: "trade", label: "Trade" },
  { id: "play", label: "Play" },
  { id: "spend", label: "Spend" },
  { id: "build", label: "Build" },
];

/**
 * Bundled fallback registry. The live list comes from /api/v1/zodiac/config so
 * a seventeenth product is one backend change rather than four redeploys; this
 * exists only so a product's header never goes blank when that call fails.
 *
 * `logoBare` is the light variant for marks that are black line art — Wall
 * Street and ReHuman are invisible on a dark panel otherwise.
 */
const FALLBACK_PRODUCTS = [
  { id: "btcy",       name: "Bitcoin Yay",         group: "earn",  href: "https://bitcoinyay.com" },
  { id: "yaysapp",    name: "YaysApp",             group: "earn",  href: "https://yaysapp.com" },
  { id: "academy",    name: "Academy",             group: "earn",  href: "https://academy.indexx.ai" },
  { id: "exchange",   name: "Exchange",            group: "trade", href: "https://cex.indexx.ai" },
  { id: "treasury",   name: "Crypto Treasury",     group: "trade", href: "https://cex.indexx.ai/crypto-treasury" },
  { id: "wallstreet", name: "Wall Street",         group: "trade", href: "https://wallstreet.indexx.ai" },
  { id: "xtokens",    name: "xTokens",             group: "trade", href: "https://indexx.ai/xtokens" },
  { id: "usdxx",      name: "USDXX",               group: "trade", href: "https://indexx.ai", comingSoon: true },
  { id: "emmm",       name: "Eeny Meeny Miny Moe", group: "play",  href: "https://eenymeenyminymoe.io" },
  { id: "lotto",      name: "Fantasy Lotto",       group: "play",  href: "https://lotto.indexx.ai" },
  { id: "pay",        name: "Indexx Pay",          group: "spend", href: "https://pay.indexx.ai" },
  { id: "wallet",     name: "Indexx Wallet",       group: "spend", href: "https://wallet.indexx.ai" },
  { id: "shoperpal",  name: "ShoperPal",           group: "spend", href: "https://shop.indexx.ai" },
  { id: "rehuman",    name: "ReHuman",             group: "spend", href: "https://indexx.ai", comingSoon: true },
  { id: "aiainai",    name: "ai ai N ai",          group: "build", href: "https://aiainai.ai" },
  { id: "xxemails",   name: "XXemails",            group: "build", href: "https://indexx.ai" },
];

/** Artwork filenames. The API registry carries ids, not art. */
const ART = {
  btcy:       { logo: "btcy.png" },
  yaysapp:    { logo: "yaysapp.png" },
  /* Academy's two files are named the opposite way round to every other pair
     here: "academy-light.png" is the art FOR a light background (black
     outlines, which vanish on the dark panel) and "academy.png" is the one with
     white outlines that reads on dark. Point each tone at the file that suits
     it rather than renaming art the product repos also reference. */
  academy:    { logo: "academy-light.png", logoBare: "academy.png" },
  exchange:   { logo: "exchange.png" },
  treasury:   { logo: "treasury.png" },
  wallstreet: { logo: "wallst.png", logoBare: "wallst-light.png" },
  xtokens:    { logo: "xtokens.png" },
  usdxx:      { logo: "usdxx.png" },
  /* Supplied dark-panel art, Sep 2026. The light-tone files stay as they were:
     the old marks are dark line art that muddies on the panel, these read on
     it. Both were cut from the brand pack — wallet's came as an SVG that was
     really a 4096x904 raster in a wrapper, and whose viewBox sliced 8px into
     the wordmark, so the symbol was re-cut from the artwork rather than used
     as shipped. */
  emmm:       { logo: "emmm.png", logoBare: "emmm-dark.png" },
  lotto:      { logo: "lotto.png" },
  pay:        { logo: "pay.png" },
  wallet:     { logo: "wallet.png", logoBare: "wallet-dark.png" },
  shoperpal:  { logo: "shoperpal.png" },
  rehuman:    { logo: "rehuman.png", logoBare: "rehuman-light.png" },
  aiainai:    { logo: "aiainai.png" },
  xxemails:   { logo: "xxemails.png" },
};

/**
 * Where the ecosystem API lives. CRA inlines REACT_APP_*, Next inlines
 * NEXT_PUBLIC_*, and the same file ships into both — so read both and let
 * whichever the host bundler replaced win. Wrapped because `process` does not
 * exist in every runtime, and an undefined reference here would take a
 * product's whole header down.
 */
const ENV_API_BASE = (() => {
  try {
    if (typeof process !== "undefined" && process.env) {
      return (
        process.env.NEXT_PUBLIC_ZODIAC_API ||
        process.env.REACT_APP_ZODIAC_API ||
        null
      );
    }
  } catch (_) {
    /* no process in this runtime */
  }
  return null;
})();

const DEFAULT_API_BASE = "https://api.v1.indexx.ai";
/* Local to whatever product you are on. The Dashboard lives INSIDE each repo —
   sending someone to another domain to read their own balances is the opposite
   of what the shell is for, and on the four foreign-domain products it would be
   a cross-origin hop mid-session. */
const DEFAULT_DASHBOARD = "/hub";

/* 5062, not 5060: 5060 is SIP and sits on the WHATWG blocked-port list, which
   browsers enforce — an origin there is simply unreachable from a page. */
const DEFAULT_IDP_ISSUER =
  (typeof process !== "undefined" && process.env &&
    (process.env.NEXT_PUBLIC_IDP_ISSUER || process.env.REACT_APP_IDP_ISSUER)) ||
  "http://localhost:5062";

/**
 * Carry the session across to another product.
 *
 * Each product is a separate ORIGIN — different domain in production, different
 * port locally — and localStorage is per-origin, so a session simply does not
 * travel. Logging in on indexx.ai and landing logged-out on Lotto is not a bug
 * in the switcher; it is the shared-session feature not existing yet.
 *
 * Until Indexx ID exists, the estate bridges this with a handoff token in the
 * query string, and every product already consumes it (`?signInToken=`). This
 * mirrors exactly what `indexx-welcome-page/src/components/Header_test:39`
 * already does for its own links, so the switcher behaves like the rest of the
 * estate rather than inventing a second mechanism.
 *
 * ── Known weaknesses, inherited deliberately ───────────────────────────────
 * The token rides in a query string (leaks via Referer, history and access
 * logs), lasts 8 hours, is reusable, and is bound to no audience. Those are the
 * reasons `tasks/ZODIAC-PLAN.md` replaces this with authorization-code + PKCE
 * in Phase 2. This is a bridge to make the demo honest, not the destination.
 */
const withSession = (href) => {
  try {
    if (typeof window === "undefined") return href;
    const url = new URL(href, window.location.origin);
    // Same origin already shares localStorage — nothing to hand off.
    if (url.origin === window.location.origin) return href;
    /*
     * localStorage holds STRINGS. A product that stored a missing token with
     * `setItem(key, null)` leaves the literal text "null" behind, which is
     * truthy — that is how these links ended up as `?signInToken=null`, handing
     * the next product a token it will fail to decode. Treat the placeholder
     * spellings as absent, the same allowlist habit the backend's `num()` uses.
     */
    const raw =
      window.localStorage.getItem("shortToken") ||
      window.localStorage.getItem("access_token");
    const handoff =
      typeof raw === "string" &&
      raw.trim() !== "" &&
      raw !== "null" &&
      raw !== "undefined"
        ? raw
        : null;
    if (!handoff) return href;
    url.searchParams.set("signInToken", handoff);
    return url.toString();
  } catch (_) {
    return href;
  }
};

/**
 * The signed-in email a bridging product already keeps. Never writes.
 *
 * Only a fallback for the panel's account row: the id_token claims are the real
 * source, and this covers the window before an exchange has happened on this
 * page load. Products that opt out of the legacy bridge simply have nothing
 * here, and the row falls back to showing nothing rather than something wrong.
 */
const readLegacyEmail = () => {
  try {
    return window.localStorage.getItem("email") || null;
  } catch (_) {
    return null;
  }
};

/**
 * Read the estate session the host product already stores. Never writes.
 *
 * Only `access_token`. It used to fall back to camelCase `accessToken`, which
 * nothing in zodiacAuth ever writes — so the only way that key exists is that
 * the PRODUCT put its own session there. Wall Street does exactly that, signed
 * by its own backend with its own secret, and the fallback handed that foreign
 * token to the estate API as a Bearer credential. Every /zodiac/hub call from
 * Wall Street 401'd for that reason, and the panel showed "explore" on all
 * sixteen rows instead of real state. A token this client did not obtain is not
 * a session it can speak for.
 */
const readToken = () => {
  try {
    return window.localStorage.getItem("access_token") || null;
  } catch (_) {
    return null;
  }
};

/**
 * The Dashboard link.
 *
 * Separate export because the bar puts the switcher hard left and this hard
 * right; one component cannot be in two places without a portal, and a portal
 * for a single link would fight the host's flex layout for no gain.
 */
export function ZodiacDashboardLink({
  dashboardUrl = DEFAULT_DASHBOARD,
  tone = "dark",
  label = "Dashboard",
}) {
  return (
    <a
      className={`${s.dash} ${tone === "light" ? s.dashLight : ""}`}
      href={withSession(dashboardUrl)}
    >
      {label}
    </a>
  );
}

/** One product row: bare mark, name, and a monospace state line. */
function Row({ product, active, note, isLive, tone, logoBase, onClose, useHandoff, onNavigate }) {
  const art = ART[product.id] || {};
  // Bare marks, no oval: on a dark panel the black line-art marks need their
  // light variant or they vanish entirely.
  const file =
    tone === "light" ? art.logo : art.logoBare || art.logo || `${product.id}.png`;

  const hint = active
    ? "you are here"
    : product.comingSoon
    ? "soon"
    : note || "explore";

  return (
    <a
      className={`${s.row} ${active ? s.rowActive : ""}`}
      href={useHandoff ? withSession(product.href) : product.href}
      aria-current={active ? "page" : undefined}
      onClick={(e) => {
        /*
         * Only a plain left click becomes a handoff. Cmd/Ctrl/Shift/Alt and the
         * middle button all mean "open this somewhere else" — swallowing those
         * to show a screen about leaving would be actively wrong, and the href
         * stays on the anchor so copy-link and open-in-new-tab keep working.
         */
        const plain =
          e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
        if (!plain || active || !onNavigate) {
          onClose();
          return;
        }
        e.preventDefault();
        onNavigate(product, e.currentTarget.href);
      }}
    >
      <span className={s.mark}>
        <img src={`${logoBase}/${file}`} alt="" aria-hidden="true" />
      </span>
      <span className={s.rowText}>
        <span className={s.rowName}>{product.name}</span>
        <span
          className={`${s.rowHint} ${
            active ? s.hintHere : isLive ? s.hintLive : ""
          }`}
        >
          {hint}
        </span>
      </span>
    </a>
  );
}

/**
 * The screen between two products.
 *
 * Sixteen products across several registrable domains means a jump is a full
 * cross-origin navigation: this page goes white, the next one takes a beat to
 * boot, and the gap reads as a dropped session rather than a move. Naming both
 * ends turns that dead time into the claim Zodiac actually makes — that these
 * are one place — and it is honest, because the wait is real either way.
 *
 * The card is aria-hidden and the region carries one plain sentence instead:
 * read out, "Fantasy Lotto, from, Academy, to" is noise, and a handoff is
 * exactly the moment a screen-reader user most needs to know where they landed.
 */
function Handoff({ from, to, logoBase, tone }) {
  const markFor = (p) => {
    const art = (p && ART[p.id]) || {};
    // A null `p` is only ever the FROM side, and the label beside it already
    // reads "Indexx" (see fromName). The mark has to agree with that word.
    const fallback = p ? `${p.id}.png` : "indexx.png";
    return tone === "light" ? art.logo || fallback : art.logoBare || art.logo || fallback;
  };
  const fromName = (from && from.name) || "Indexx";

  return (
    <div
      className={`${s.xfade} ${tone === "light" ? s.xfadeLight : ""}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={`Moving from ${fromName} to ${to.name}. Still signed in.`}
    >
      <div className={s.xcard} aria-hidden="true">
        <div className={s.xbrand}>
          {/*
            The Indexx mark, not the Zodiac one.

            Zodiac is the plumbing — the thing that carried the session across.
            What the person is leaving and arriving at is Indexx, and this card
            is the only moment in the jump where a brand is on screen at all.
            Same 220x189 art box as zodiac.png, so the 26px rule is unchanged.
          */}
          <img className={s.xbrandMark} src={`${logoBase}/indexx.png`} alt="" />
          <span className={s.xlabel}>Moving you across</span>
        </div>

        <div className={s.xpair}>
          <span className={s.xnode}>
            <span className={s.xmark}>
              <img src={`${logoBase}/${markFor(from)}`} alt="" />
            </span>
            <span className={s.xname}>{fromName}</span>
            <span className={s.xrole}>from</span>
          </span>

          <svg className={s.xarrow} width="26" height="10" viewBox="0 0 26 10" fill="none">
            <path
              d="M0 5h22M18.5 1.2 22.8 5l-4.3 3.8"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          <span className={s.xnode}>
            <span className={s.xmark}>
              <img src={`${logoBase}/${markFor(to)}`} alt="" />
            </span>
            <span className={s.xname}>{to.name}</span>
            <span className={s.xrole}>to</span>
          </span>
        </div>

        <span className={s.xbar}>
          <span className={s.xbarFill} />
        </span>
        <p className={s.xfoot}>
          Signed in here, signed in everywhere. No product asks you to log in again.
        </p>
      </div>
    </div>
  );
}

export default function ZodiacLauncher({
  /** Marks the current product in the panel. */
  productId,
  dashboardUrl = DEFAULT_DASHBOARD,
  /**
   * Explicit prop wins, then the build-time env var, then production.
   *
   * The default is load-bearing TWICE, which is why it is a cast and not a
   * bare `undefined`. Without ANY default, TypeScript infers the prop as
   * REQUIRED in the repos that set `allowJs`, and every call site omitting it
   * fails with TS2741. With a bare `= undefined` it infers the type as
   * literally `undefined`, and every call site PASSING one fails with TS2322 —
   * which is what broke ai ai N ai's build (Sidebar.tsx:658) and would have
   * broken Wall Street's the moment it type-checked (UniversalSidebar.tsx:624).
   * The cast satisfies both.
   */
  apiBase = /** @type {any} */ (undefined),
  tone = "dark",
  /**
   * "nav"  — a sidebar row that inherits the host's colour and type.
   * "icon" — the mark alone, sized for a narrow icon rail (YaysApp Web's, and
   *          whatever the mobile shells end up needing).
   * See .triggerNav / .triggerIcon in the stylesheet.
   */
  variant = /** @type {any} */ (undefined),
  logoBase = "/logos",
  /**
   * Per-platform palette, applied as CSS custom properties.
   *
   * The `/** @type {any} *\/ ({})` default is load-bearing, not noise. Both CRA
   * repos type-check this .jsx file (`allowJs`), and TypeScript infers a prop's
   * type from its default: `= undefined` infers the type `undefined`, so every
   * call site passing a real object fails with TS2322 — the same trap that made
   * `apiBase` infer as REQUIRED earlier. An `any` cast is the portable fix; it
   * is a comment to Babel and Next, and a type to tsc.
   */
  theme = /** @type {any} */ ({}),
  /**
   * Indexx ID wiring: { issuer?, clientId }. Omit it and the launcher behaves
   * exactly as before — products mid-migration are not broken by its absence.
   */
  auth = /** @type {any} */ (null),
  /** Render the Dashboard link inside the trigger cluster. Off by default —
   *  the bar normally places <ZodiacDashboardLink /> on the far right. */
  withDashboard = false,
  /**
   * Clear THIS product's own session, before the shared one is ended.
   *
   * The panel's Sign out revokes the Indexx ID session for the whole estate,
   * which is the part no product could do for itself. What it cannot do is
   * clear a session the product keeps in its own shape: Wall Street signs its
   * own tokens, YaysApp keeps a store, ShoperPal and ai ai N ai hold cookies
   * from their own backends — none of which this component knows about. Those
   * products pass this callback so the two halves end together; without it they
   * would be signed out of the estate while their own header still said
   * otherwise. Awaited, and a rejection never blocks the sign-out.
   */
  onSignOut = /** @type {any} */ (null),
}) {
  const resolvedApiBase = apiBase || ENV_API_BASE || DEFAULT_API_BASE;

  /*
   * Say so when the registry is about to be fetched from production while the
   * page itself is local.
   *
   * ENV_API_BASE is read from `process.env`, which Vite does not populate — so
   * a Vite app that forgets the `apiBase` prop silently falls back to
   * api.v1.indexx.ai and every row in the panel carries a production url. That
   * is hard to spot, because the ISSUER default falls back to localhost
   * instead, so sign-in keeps working and only the destinations are wrong.
   * XXemails shipped exactly that. A warning costs nothing and turns a
   * puzzling afternoon into one line in the console.
   */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const local = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
    if (local && /^https:\/\/api\.v\d+\.indexx\.ai/.test(String(resolvedApiBase))) {
      // eslint-disable-next-line no-console
      console.warn(
        "[zodiac] running on " + window.location.host + " but the registry is " +
        resolvedApiBase + " — every product link will point at production. " +
        "Vite apps must pass apiBase={import.meta.env.VITE_ZODIAC_API}; " +
        "CRA/Next read REACT_APP_ZODIAC_API / NEXT_PUBLIC_ZODIAC_API."
      );
    }
  }, [resolvedApiBase]);

  /**
   * Silent sign-on.
   *
   * This is the whole point of the shell: sign in once at the IdP, and every
   * product recognises you without asking again. The launcher is the right
   * place for it because it is the one component on every page of every
   * product — so wiring it here wires the whole estate.
   *
   * The redirect to /authorize?prompt=none is a TOP-LEVEL navigation, which is
   * what makes it work for the four products on foreign domains where a
   * .indexx.ai cookie cannot reach and third-party cookies are dead.
   *
   * Two things must not happen, and both are guarded:
   *  - Never on the callback route. That page is mid-exchange; redirecting out
   *    of it would drop the authorization code on the floor.
   *  - Never twice in one page load. zodiacAuth's own guard handles the
   *    login_required case, which would otherwise be an infinite reload for
   *    every signed-out visitor on every product at once.
   */
  const authClientRef = useRef(null);

  useEffect(() => {
    if (!auth || !auth.clientId) return;
    if (typeof window === "undefined") return;
    if (window.location.pathname.startsWith("/auth/callback")) return;
    try {
      const client = createAuth({
        issuer: auth.issuer || DEFAULT_IDP_ISSUER,
        clientId: auth.clientId,
        redirectUri: window.location.origin + "/auth/callback",
        /*
         * The legacy bridge writes `access_token`, `email`, `user` and
         * `username` into localStorage so a product's EXISTING header sees the
         * session. That is right for products on the shared backend and WRONG
         * for one that keeps its own: Wall Street stores a JSON object under
         * `user`, and writing an email string there made its own
         * JSON.parse throw and took the whole app down.
         *
         * Opt out per product rather than guessing from the key's contents.
         */
        bridgeLegacyKeys:
          auth.bridgeLegacyKeys === undefined ? true : auth.bridgeLegacyKeys,
        /* Products that keep their session in their own shape adapt it here.
           See the onSession comment in zodiacAuth.js. */
        onSession: auth.onSession,
        /*
         * Whether a session appearing should reload the page.
         *
         * Defaults ON, because a product whose header reads localStorage once
         * at mount has no other way to notice a session that arrived after it
         * painted. Products that listen for the "zodiac:session" event instead
         * pass false, and lose the reload — with it the second /token grant and
         * second /session/legacy mint it forced, and the visible "Signing you
         * in…" flash. See applyEstateSession in zodiacAuth.js.
         */
        reloadOnSession:
          auth.reloadOnSession === undefined ? true : auth.reloadOnSession,
      });
      authClientRef.current = client;
      /* Expose for diagnosis: `window.__zodiacAuth.debug()` in the console says
         exactly why a silent check did or did not happen. Read-only, and it
         holds no token — the client keeps those in its own closure. */
      try { window.__zodiacAuth = client; } catch (_) {}
      client.silentSignIn().catch(() => {
        /* signed out is a normal outcome, not an error */
      });
      /* Adopt a session the product established through its OWN login form.
         The product's login UI is untouched — users see exactly the page they
         always have — and the shell turns that into an IdP session so the other
         products recognise it. See adoptLocalSession in zodiacAuth.js. */
      client.adoptLocalSession().catch(() => {});
      /*
       * Products with their OWN identity donate here. adoptLocalSession only
       * understands an estate token; Wall Street and ShoperPal sign their own
       * sessions, so they hand this provider their token and it verifies with
       * them directly. Same three moments as adopt: arriving, reaching for the
       * switcher, and leaving.
       */
      if (auth && typeof auth.donateSession === "function") {
        try {
          client.donateProductSession(auth.donateSession).catch(() => {});
        } catch (_) {
          /* never let sign-on wiring take a header down */
        }
      }

    } catch (_) {
      /* never let sign-on wiring take a product's header down */
    }
    return undefined;
    /* Depend on the VALUES, not the object. Headers pass this prop inline —
       `auth={{ clientId: "indexx-lotto" }}` — which is a new object identity on
       every render, so depending on `auth` itself re-runs this effect for every
       render and fires a concurrent silent sign-in each time. createAuth() is
       memoised per tab so that is no longer a logout, but there is still no
       reason to ask more than once per page load. */
  }, [auth && auth.issuer, auth && auth.clientId]);
  const [open, setOpen] = useState(false);
  /*
   * "This product could not be linked automatically."
   *
   * Set when the provider refused to donate this product's own session because
   * an Indexx account already uses the same email and this product does not
   * verify email addresses at signup — see donateProductSession in zodiacAuth.
   * Read when the panel opens rather than held live: that is the only moment it
   * is shown, and it is the moment the person is trying to move somewhere.
   */
  const [needsLink, setNeedsLink] = useState(false);
  /** The product being navigated TO while the handoff screen is up. */
  const [handoffTo, setHandoffTo] = useState(null);
  const handoffTimers = useRef([]);
  const [mounted, setMounted] = useState(false);
  const [products, setProducts] = useState(FALLBACK_PRODUCTS);
  /* The in-flight /config request, and its result. See the fetch effect. */
  const configPromise = useRef(null);
  const liveProducts = useRef(null);
  const [states, setStates] = useState({});
  const [anchor, setAnchor] = useState({ top: 64, left: 14, dropUp: false });
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const loadedHub = useRef(false);
  /* Who the panel says you are. Read on open, never polled. */
  const [account, setAccount] = useState(null);
  /*
   * The tooltip's viewport position, or null when hidden.
   *
   * A real element rather than the `title` attribute, for one reason: `title`
   * is shown by the browser after a delay of roughly a second that no page can
   * configure or remove. For the one control in these headers whose meaning is
   * not conventional — nine ovals — a label that arrives a second late is a
   * label most people never see. This appears on the first frame.
   */
  const [tip, setTip] = useState(null);

  useEffect(() => setMounted(true), []);

  /* Timers must not outlive the component: a handoff that is still pending when
     the header unmounts would navigate a page the user has already left. */
  useEffect(
    () => () => {
      handoffTimers.current.forEach(clearTimeout);
      handoffTimers.current = [];
    },
    []
  );

  /**
   * Show the handoff screen, then go.
   *
   * The hold is deliberately short. This is a label on a wait that is happening
   * anyway, not a delay added for its own sake — long enough to read two names,
   * and gone before it becomes the thing you remember about the jump.
   */
  const beginHandoff = useCallback(
    async (product, href) => {
      setOpen(false);
      setHandoffTo(product);

      /*
       * Last chance to donate this product's own session before we leave it.
       * The handoff screen is already up, so the round trip is free wall-clock
       * — and without it, a user who signed in here lands signed-out there.
       */
      if (authClientRef.current) {
        try {
          await authClientRef.current.adoptLocalSession();
        } catch (_) {
          /* a failed adopt must never block the navigation */
        }
      }
      /* Through the ref, and guarded: see the note at the toggle's donate. */
      if (authClientRef.current && auth && typeof auth.donateSession === "function") {
        try {
          await authClientRef.current.donateProductSession(auth.donateSession);
        } catch (_) {
          /* likewise — never block the move */
        }
      }

      /*
       * Re-resolve the destination from the live registry.
       *
       * FALLBACK_PRODUCTS ships PRODUCTION urls — right when the API is
       * unreachable, wrong for the first moments of every page load, when the
       * panel is already open and clickable but /config has not answered yet.
       * Click in that window and you leave localhost for the real site. It has
       * happened three times in testing, which is three more than a race this
       * cheap to close deserves.
       *
       * The handoff screen is already up, so this wait costs no wall-clock the
       * user can see. The timeout keeps a hung /config from stranding them:
       * fall through to the bundled url, which is the honest answer when the
       * registry cannot be reached at all.
       */
      if (configPromise.current) {
        try {
          await Promise.race([
            configPromise.current,
            new Promise((resolve) => setTimeout(resolve, 2500)),
          ]);
        } catch (_) {
          /* fall through to whatever href we were given */
        }
      }
      if (Array.isArray(liveProducts.current) && product && product.id) {
        const fresh = liveProducts.current.find((p) => p && p.id === product.id);
        if (fresh && fresh.href) href = fresh.href;
      }

      const reduce =
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      handoffTimers.current.push(
        setTimeout(() => {
          try {
            window.location.href = href;
          } catch (_) {
            setHandoffTo(null);
          }
        }, reduce ? 180 : 620)
      );

      /*
       * Safety net. Everything above assumes the browser actually leaves this
       * page, and almost always it does — but an href that resolves to this
       * very URL, or a navigation the browser declines, would otherwise strand
       * the user behind a full-screen overlay with no way out. Give it up.
       */
      handoffTimers.current.push(setTimeout(() => setHandoffTo(null), 8000));
    },
    []
  );

  useEffect(() => {
    let alive = true;
    const url = `${String(resolvedApiBase).replace(/\/+$/, "")}/api/v1/zodiac/config`;
    /*
     * Held as a promise, not just fired, because beginHandoff awaits it — see
     * the note there. The ref is what that read uses: state would be stale
     * inside a callback created before the fetch resolved.
     */
    configPromise.current = fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        const live = body && body.data && body.data.products;
        if (Array.isArray(live) && live.length) {
          const products = live.map((product) =>
            product.id === "pay"
              ? { ...product, href: "https://pay.indexx.ai", comingSoon: false }
              : product
          );
          liveProducts.current = products;
          if (alive) setProducts(products);
        }
        return Array.isArray(live) && live.length ? liveProducts.current : live;
      })
      .catch(() => {
        /* keep the bundled list */
        return null;
      });
    return () => {
      alive = false;
    };
  }, [resolvedApiBase]);

  /**
   * The per-product state lines are fetched ONLY when the panel opens.
   *
   * This bar renders on every page of every product; a sixteen-way fan-out on
   * every page load would be indefensible. Opening the switcher is a deliberate
   * act, so that is where the cost belongs. Unauthenticated or failed, the rows
   * fall back to "explore" — never to an invented figure.
   */
  const loadHub = useCallback(() => {
    if (loadedHub.current) return;
    const token = readToken();
    if (!token) return;
    loadedHub.current = true;
    fetch(`${String(resolvedApiBase).replace(/\/+$/, "")}/api/v1/zodiac/hub`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        const rows = body && body.data && body.data.products;
        if (!Array.isArray(rows)) return;
        const next = {};
        rows.forEach((p) => {
          next[p.id] = { note: p.note, active: p.active, status: p.status };
        });
        setStates(next);
      })
      .catch(() => {
        /* rows stay on "explore" */
      });
  }, [resolvedApiBase]);

  const close = useCallback(() => setOpen(false), []);

  /*
   * Who is signed in, read at the moment the panel opens.
   *
   * Deliberately not state the launcher maintains: the claims live in the auth
   * client, the product may have signed in through its own form since this
   * component mounted, and the panel is the only place this is rendered. Reading
   * on open is both the cheapest and the freshest option.
   *
   * These are id_token claims — display data, never an authorization decision.
   */
  const readAccount = useCallback(() => {
    const client = authClientRef.current;
    if (!client) return;
    try {
      const user = typeof client.getUser === "function" ? client.getUser() : null;
      const email = (user && user.email) || readLegacyEmail();
      if (!email) return setAccount(null);
      const name = (user && (user.name || user.preferred_username)) || null;
      setAccount({ email: email, name: name });
    } catch (_) {
      setAccount(null);
    }
  }, []);

  /*
   * Position from the trigger's own rect, measured at the moment it is shown.
   *
   * Fixed coordinates and portalled to <body>, the same reason the panel is:
   * every one of these headers has an ancestor with overflow, and a tooltip
   * rendered inside the button would be clipped by it.
   *
   * Nothing is shown while the panel is open — the panel names itself, and a
   * tooltip over it is noise.
   */
  const showTip = useCallback(() => {
    if (open) return;
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    /*
     * Anchor to whichever edge keeps it on screen.
     *
     * Centred under the trigger is the nice default and the wrong one here:
     * this control sits in the TOP-RIGHT corner of every product, so a centred
     * label ran off the right of the viewport — measured 1496px on a 1440px
     * window. Deciding from the trigger's own position needs no measurement of
     * the tooltip itself, so there is no second pass and nothing flickers.
     */
    const vw = window.innerWidth;
    const centre = r.left + r.width / 2;
    const top = r.bottom + 8;
    if (centre > vw * 0.72) return setTip({ top, right: Math.max(8, vw - r.right), place: "end" });
    if (centre < vw * 0.28) return setTip({ top, left: Math.max(8, r.left), place: "start" });
    setTip({ top, left: centre, place: "center" });
  }, [open]);

  const hideTip = useCallback(() => setTip(null), []);

  const toggle = () => {
    /*
     * Re-check for a session the product established on its OWN login form.
     *
     * adoptLocalSession turns that into an Indexx ID session, and this is the
     * moment it matters: the user has just signed in here and is reaching for
     * the switcher to go somewhere else. Running it only at mount missed that
     * entirely — at mount they were still signed out.
     *
     * ── allowNavigation: false, and why it is not optional ────────────────
     *
     * This used to be described as "cheap and idempotent". It was neither.
     * adoptLocalSession spends the provider's adopt_code on a TOP-LEVEL
     * navigation, so opening the panel could take the whole tab with it —
     * measured on BTCY: one click on the waffle produced `/` ->
     * IDP/authorize -> /auth/callback -> `/`. The user sees the header behind
     * the open panel fall back to Login/Register for the round trip, and the
     * panel loses its own state because the app remounted under it. Reported
     * as "when I click the zodiac switcher the authentication is gone".
     *
     * Opening a menu must never navigate. The POST still runs, so every
     * product sharing a registrable domain with the provider still gets its
     * cookie here; the foreign domains that genuinely need the round trip get
     * it in beginHandoff, on the way out, where a navigation is the point.
     */
    if (authClientRef.current) {
      try {
        authClientRef.current.adoptLocalSession({ allowNavigation: false }).catch(() => {});
      } catch (_) {
        /* never let sign-on wiring block the panel from opening */
      }
    }
    if (authClientRef.current) {
      try {
        setNeedsLink(Boolean(authClientRef.current.linkRequired()));
      } catch (_) {
        /* diagnostics only — never let this take the panel down */
      }
    }
    /* The ref, not `client`: that binding belongs to the mount effect and does
       not exist out here. Check it — the try/catch below would swallow a null
       deref, and a swallowed TypeError reads exactly like "this product had
       nothing to donate". */
    if (authClientRef.current && auth && typeof auth.donateSession === "function") {
      try {
        authClientRef.current.donateProductSession(auth.donateSession).catch(() => {});
      } catch (_) {
        /* same */
      }
    }

    setOpen((wasOpen) => {
      if (wasOpen) return false;
      // The panel is FIXED and portalled, so it needs viewport coordinates from
      // the trigger — an absolutely-positioned panel is clipped by any ancestor
      // with overflow, and every one of these headers has one.
      const box = triggerRef.current && triggerRef.current.getBoundingClientRect();
      if (box) {
        // Clamp into the viewport. The panel is up to 900px and portalled to
        // <body>, so on a laptop or phone an un-clamped left taken straight from
        // the trigger pushes it off the right edge — the switcher then looks
        // broken on exactly the screens most people use.
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const width = Math.min(900, vw - 24);

        /*
         * Drop up when there is no room to drop down.
         *
         * The panel is tall and the trigger is not always near the top of the
         * screen — in Wall Street's sidebar it sits at the BOTTOM, so anchoring
         * to box.bottom put the panel mostly below the fold with its lower half
         * unreachable. Flip it above the trigger when that is the roomier side,
         * and cap the height to whichever gap it actually lands in so it can
         * scroll instead of being cut off either way.
         */
        const below = vh - box.bottom;
        const above = box.top;
        const dropUp = below < 360 && above > below;

        setAnchor({
          top: box.bottom,
          bottom: vh - box.top,
          left: Math.max(12, Math.min(box.left, vw - width - 12)),
          dropUp: dropUp,
          maxHeight: Math.max(220, (dropUp ? above : below) - 16),
        });
      }
      loadHub();
      readAccount();
      return true;
    });
  };

  /*
   * Sign out of everything.
   *
   * Order matters and is the whole point. The product clears its own session
   * first, because `signOut` NAVIGATES to the provider's end-session endpoint
   * and may never come back — anything left until after it may simply not run.
   * Only then is the shared session revoked, which is the half no product could
   * do alone: clearing local storage ends a session in one tab and nowhere
   * else, so the next page load silently signs the person back in.
   */
  const signOutEverywhere = useCallback(async () => {
    if (typeof onSignOut === "function") {
      try {
        await onSignOut();
      } catch (_) {
        /* The product's own cleanup failing must not trap the user signed in. */
      }
    }
    const client = authClientRef.current;
    if (!client || typeof client.signOut !== "function") {
      /* Nothing to revoke — at least do not leave the panel claiming a session. */
      setAccount(null);
      setOpen(false);
      return;
    }
    try {
      await client.signOut();
    } catch (_) {
      /* Best effort: signOut never throws by contract, but never trap the user. */
    }
  }, [onSignOut]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        close();
        if (triggerRef.current) triggerRef.current.focus();
      }
    };
    const onClick = (e) => {
      // The panel is PORTALLED to <body>, so it is not inside rootRef. Checking
      // only rootRef meant a mousedown on a product link counted as "outside",
      // closed the panel, and unmounted the <a> before the click completed — so
      // the panel shut and nothing navigated. Both subtrees have to count as
      // inside; the scrim closes via its own onClick.
      const t = e.target;
      const inRoot = rootRef.current && rootRef.current.contains(t);
      const inPanel = panelRef.current && panelRef.current.contains(t);
      if (!inRoot && !inPanel) close();
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open, close]);

  /*
   * Hold the page still while the panel is open.
   *
   * The panel is fixed and portalled to <body>, so the document behind it stays
   * perfectly scrollable: a wheel over the scrim moved the page, and once the
   * panel's own list hit its end the scroll CHAINED out to the document and
   * carried on. Either way the user came back to a different place than they
   * left, and the panel read as pasted on top of the page rather than as a
   * layer of it.
   *
   * `position: fixed` on <body> rather than `overflow: hidden`, because
   * overflow alone does not hold iOS Safari, which scrolls the document anyway.
   * Pinning the body collapses it to the viewport, so the offset has to be put
   * back as a negative `top` or the page jumps to the top the moment it locks —
   * and restored on the way out, which is the part that makes the position
   * survive rather than merely freeze.
   *
   * The scrollbar disappearing with it would reflow everything a few pixels
   * wider, so its width is added back as padding. Measured, not assumed:
   * overlay scrollbars (macOS default, all of mobile) have zero width and must
   * get zero padding.
   *
   * Every value read here is the INLINE style, and every one is put back
   * exactly as found — including "was not set at all", which restores to "".
   * A product that sets its own body styles must not have them rewritten by a
   * switcher being opened.
   */
  useEffect(() => {
    if (!open) return undefined;
    if (typeof window === "undefined" || !document.body) return undefined;

    const body = document.body;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const barWidth = window.innerWidth - document.documentElement.clientWidth;

    const prior = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      paddingRight: body.style.paddingRight,
    };

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    if (barWidth > 0) {
      const existing = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${existing + barWidth}px`;
    }

    return () => {
      Object.keys(prior).forEach((k) => {
        body.style[k] = prior[k];
      });
      /*
       * Put them back where they were. Instant, not smooth: a product with
       * `scroll-behavior: smooth` would otherwise animate the restore and the
       * page would visibly slide as the panel closes.
       */
      try {
        window.scrollTo({ top: scrollY, left: 0, behavior: "instant" });
      } catch (_) {
        window.scrollTo(0, scrollY);
      }
    };
  }, [open]);

  const inGroup = (id) => products.filter((p) => p.group === id);

  const panel = (
    <>
      <div className={s.scrim} aria-hidden="true" onClick={close} />
      <div
        ref={panelRef}
        className={`${s.panel} ${tone === "light" ? s.panelLight : ""}`}
        role="dialog"
        aria-label="Indexx ecosystem"
        style={{
          ...(anchor.dropUp
            ? { bottom: anchor.bottom + 6 }
            : { top: anchor.top + 6 }),
          left: anchor.left,
          maxHeight: anchor.maxHeight,
          ...(theme || {}),
        }}
      >
        {/*
          The name leads, on its own.

          The mark used to sit here at full size, on the reasoning that this is
          the moment someone learns the ecosystem has a name. But the same mark
          IS the button that opened this panel, a few pixels up and still on
          screen — showing it twice explained nothing and took weight off the
          one thing in this row that does the explaining, which is the name.
        */}
        <div className={s.panelHead}>
          <span className={s.panelTitleWrap}>
            <span className={s.panelTitle}>Indexx Zodiac</span>
            <span className={s.panelSub}>
              The Indexx ecosystem — one ID across all sixteen
            </span>
          </span>
          <span className={s.panelCount}>
            {products.length} products · {GROUPS.length} groups
          </span>
        </div>

        {/*
          The one refusal a person can actually do something about.

          This product signs its own sessions and does not verify email at
          signup, so the provider will not hand it an estate account that
          already uses the same address — that would be claiming an account
          rather than creating one. The refusal is correct and it stays. What
          was wrong is that it was invisible: the call failed silently and the
          switcher simply never signed you in anywhere, with no way to find out
          why.

          Signing in with Indexx once proves the identity. After that this
          product's own bridge records the subject, and every later sign-in
          here connects on its own.

          Offered, not forced: throwing someone off the page they are on to a
          login screen they did not ask for is worse than the silence was. This
          is where they already are when they try to move.
        */}
        {needsLink ? (
          <div className={s.linkNotice}>
            <span className={s.linkNoticeText}>
              An Indexx account already uses this email. Sign in with Indexx
              once to connect them — after that, signing in here signs you in
              everywhere.
            </span>
            <button
              type="button"
              className={s.linkNoticeAction}
              onClick={() => {
                try {
                  if (authClientRef.current) authClientRef.current.signIn();
                } catch (_) {
                  /* a failed sign-in must never break the panel */
                }
              }}
            >
              Sign in with Indexx
            </button>
          </div>
        ) : null}

        <div className={s.groups}>
          {GROUPS.map((g) => {
            const items = inGroup(g.id);
            if (!items.length) return null;
            const wide = g.id === "spend";
            return (
              <div key={g.id} className={wide ? s.groupWide : s.group}>
                <div className={s.groupLabelRow}>
                  <span className={s.groupLabel}>{g.label}</span>
                  <span className={s.rule} />
                </div>
                <div className={wide ? s.rowsWide : s.rows}>
                  {items.map((p) => {
                    const st = states[p.id] || {};
                    return (
                      <Row
                        key={p.id}
                        product={p}
                        active={p.id === productId}
                        note={st.note}
                        isLive={Boolean(st.active)}
                        tone={tone}
                        logoBase={logoBase}
                        onClose={close}
                        onNavigate={beginHandoff}
                        useHandoff={!auth || !auth.clientId}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className={s.panelFoot}>
          {account ? (
            /*
             * The panel promised "signed in everywhere" and offered no way out
             * of it. Clearing local storage — which is what every product's own
             * Logout did — ends a session in one tab and nowhere else: the
             * provider's cookie survives, so the next page load signs the person
             * straight back in. On a shared machine the next person inherits it.
             * This is the control that actually ends the shared session, and it
             * belongs here because here is the only place that claims to span
             * all sixteen products.
             */
            <>
              <span className={s.account}>
                <span className={s.accountEmail} title={account.email}>
                  {account.name || account.email}
                </span>
              </span>
              <button
                type="button"
                className={s.authBtn}
                onClick={signOutEverywhere}
              >
                Sign out everywhere
              </button>
            </>
          ) : (
            "Signed in here, signed in everywhere. No product asks you to log in again."
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className={`${s.root} ${tone === "light" ? s.light : ""}`} ref={rootRef}>
      {/*
        A dot grid, because that shape already means "all the apps" to anyone
        who has used a Google product — the recognition is borrowed, not taught.
        The ovals carry the system's own -38.4° tilt and Zodiac's green, so it
        reads as ours rather than as a copy. The word sits beside it because
        nothing in nine dots says the name.
      */}
      <button
        type="button"
        ref={triggerRef}
        className={`${s.trigger} ${variant === "nav" ? s.triggerNav : ""} ${variant === "icon" ? s.triggerIcon : ""} ${open ? s.triggerOpen : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        /*
         * `title` and `aria-label` carry the SAME words on purpose.
         *
         * There was no title at all, so the control had no tooltip and nothing
         * on hover said what the nine ovals were — the one icon in the header
         * whose meaning is not conventional. aria-label overrides title for
         * assistive technology, so letting them differ would mean a screen
         * reader and a sighted user being told two different things about the
         * same button.
         */
        /*
         * No `title`. It would render a SECOND, OS-drawn tooltip a beat after
         * this one, saying the same thing twice in two places.
         *
         * The accessible name is unaffected: `aria-label` is what assistive
         * technology reads, and the element below is aria-hidden because it is
         * a visual echo of that same name.
         */
        aria-label={TRIGGER_LABEL}
        onClick={toggle}
        onMouseEnter={showTip}
        onMouseLeave={hideTip}
        onFocus={showTip}
        onBlur={hideTip}
      >
        <span className={s.grid} aria-hidden="true">
          {Array.from({ length: 9 }).map((_, i) => (
            <i key={i} style={{ transform: `rotate(${OVAL_ANGLE}deg)` }} />
          ))}
        </span>
        {/* The icon variant is the mark alone — for a narrow icon rail, where
            the word does not fit and gets clipped to "Zo". The button keeps its
            aria-label, so it is still announced as "Indexx ecosystem". */}
        {/*
            The mark alone, by default.

            The nine ovals ARE the brand — the word beside them was belt and
            braces while the shape was unfamiliar, and it is not any more. The
            name now lives in the panel that opens, where there is room to say it
            properly ("Indexx Zodiac") instead of abbreviating it into a corner.

            `variant="nav"` keeps the word: there it is a row in a list of
            labelled rows, and a lone mark among labels reads as a mistake
            rather than as restraint. It spells the name in FULL, matching the
            panel — "Zodiac" alone was the abbreviation we just stopped using. The button keeps its aria-label either way,
            so it is always announced as "Indexx ecosystem".
        */}
        {variant === "nav" ? <span className={s.word}>Indexx Zodiac</span> : null}
      </button>

      {withDashboard && (
        <ZodiacDashboardLink dashboardUrl={dashboardUrl} tone={tone} />
      )}

      {tip && !open && mounted && typeof document !== "undefined"
        ? createPortal(
            <span
              className={`${s.tip} ${tone === "light" ? s.tipLight : ""} ${
                tip.place === "end" ? s.tipEnd : tip.place === "start" ? s.tipStart : ""
              }`}
              style={
                tip.place === "end"
                  ? { top: tip.top, right: tip.right }
                  : { top: tip.top, left: tip.left }
              }
              role="presentation"
              aria-hidden="true"
            >
              {TRIGGER_LABEL}
            </span>,
            document.body
          )
        : null}

      {open && mounted && typeof document !== "undefined"
        ? createPortal(panel, document.body)
        : null}

      {handoffTo && mounted && typeof document !== "undefined"
        ? createPortal(
            <Handoff
              from={products.find((p) => p.id === productId) || null}
              to={handoffTo}
              logoBase={logoBase}
              tone={tone}
            />,
            document.body
          )
        : null}
    </div>
  );
}
